import { IssueSourceType, IssueState } from '@/types';
import {
    TriageFilterValues,
    TriageInsight,
    TriageReleaseFilter,
    TriageSortBy,
    TriageStatusGroup,
} from '@/actions/triage';
import { DEFAULT_TRIAGE_FILTERS } from '@/reducers/triageFilters';
import { parseCsvParam } from '@/helpers/urlParamHelpers';

export const TRIAGE_QUERY_PARAMS = {
    statusGroup: 'status',
    sortBy: 'sort',
    sortOrder: 'sortOrder',
    releaseFilter: 'release',
    search: 'q',
    page: 'page',
    statuses: 'statuses',
    sources: 'sources',
    owners: 'owners',
    reporters: 'reporters',
    versions: 'versions',
    destinations: 'destinations',
    insights: 'insights',
} as const;

const STATUS_GROUPS: TriageStatusGroup[] = ['open', 'closed'];
const SORT_BYS: TriageSortBy[] = ['triage', 'date', 'destinations', 'owner', 'source'];
const RELEASE_FILTERS: TriageReleaseFilter[] = ['none', 'live', 'next'];
const INSIGHTS: TriageInsight[] = ['relatedIssues', 'potentialCauses'];

const parseEnumCsvParam = <T extends string>(value: string | null, allowed: readonly T[]): T[] =>
    parseCsvParam(value).filter((item): item is T => allowed.includes(item as T));

const parseEnumParam = <T extends string>(value: string | null, allowed: readonly T[], fallback: T): T =>
    value && allowed.includes(value as T) ? (value as T) : fallback;

const parsePageParam = (value: string | null): number => {
    const page = Number(value);
    return Number.isInteger(page) && page > 0 ? page : 1;
};

export const parseTriageParamsFromSearch = (search: string): { filters: TriageFilterValues; page: number } => {
    const params = new URLSearchParams(search);
    const defaults = DEFAULT_TRIAGE_FILTERS;
    return {
        filters: {
            statusGroup: parseEnumParam(
                params.get(TRIAGE_QUERY_PARAMS.statusGroup),
                STATUS_GROUPS,
                defaults.statusGroup,
            ),
            sortBy: parseEnumParam(params.get(TRIAGE_QUERY_PARAMS.sortBy), SORT_BYS, defaults.sortBy),
            sortOrder: parseEnumCsvParam(params.get(TRIAGE_QUERY_PARAMS.sortOrder), Object.values(IssueState)),
            releaseFilter: parseEnumParam(
                params.get(TRIAGE_QUERY_PARAMS.releaseFilter),
                RELEASE_FILTERS,
                defaults.releaseFilter,
            ),
            search: params.get(TRIAGE_QUERY_PARAMS.search)?.trim() ?? defaults.search,
            statuses: parseEnumCsvParam(params.get(TRIAGE_QUERY_PARAMS.statuses), Object.values(IssueState)),
            sources: parseEnumCsvParam(params.get(TRIAGE_QUERY_PARAMS.sources), Object.values(IssueSourceType)),
            owners: parseCsvParam(params.get(TRIAGE_QUERY_PARAMS.owners)),
            reporters: parseCsvParam(params.get(TRIAGE_QUERY_PARAMS.reporters)),
            versions: parseCsvParam(params.get(TRIAGE_QUERY_PARAMS.versions)),
            destinations: parseCsvParam(params.get(TRIAGE_QUERY_PARAMS.destinations)),
            insights: parseEnumCsvParam(params.get(TRIAGE_QUERY_PARAMS.insights), INSIGHTS),
        },
        page: parsePageParam(params.get(TRIAGE_QUERY_PARAMS.page)),
    };
};

export const buildTriageSearch = (filters: TriageFilterValues, page: number, search: string): string => {
    const params = new URLSearchParams(search);
    const defaults = DEFAULT_TRIAGE_FILTERS;

    const setOrDelete = (key: string, value: string, isDefault: boolean) => {
        if (isDefault) {
            params.delete(key);
        } else {
            params.set(key, value);
        }
    };
    const setCsv = (key: string, values: string[]) => setOrDelete(key, values.join(','), values.length === 0);

    setOrDelete(TRIAGE_QUERY_PARAMS.statusGroup, filters.statusGroup, filters.statusGroup === defaults.statusGroup);
    setOrDelete(TRIAGE_QUERY_PARAMS.sortBy, filters.sortBy, filters.sortBy === defaults.sortBy);
    setCsv(TRIAGE_QUERY_PARAMS.sortOrder, filters.sortBy === 'triage' ? filters.sortOrder : []);
    setOrDelete(
        TRIAGE_QUERY_PARAMS.releaseFilter,
        filters.releaseFilter,
        filters.releaseFilter === defaults.releaseFilter,
    );
    setOrDelete(TRIAGE_QUERY_PARAMS.search, filters.search, filters.search === defaults.search);
    setOrDelete(TRIAGE_QUERY_PARAMS.page, String(page), page <= 1);
    setCsv(TRIAGE_QUERY_PARAMS.statuses, filters.statuses);
    setCsv(TRIAGE_QUERY_PARAMS.sources, filters.sources);
    setCsv(TRIAGE_QUERY_PARAMS.owners, filters.owners);
    setCsv(TRIAGE_QUERY_PARAMS.reporters, filters.reporters);
    setCsv(TRIAGE_QUERY_PARAMS.versions, filters.versions);
    setCsv(TRIAGE_QUERY_PARAMS.destinations, filters.destinations);
    setCsv(TRIAGE_QUERY_PARAMS.insights, filters.insights);

    return params.toString().replace(/%2C/g, ',');
};
